/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Container, CircularProgress, Typography, Button, Box, Grid2 } from '@mui/material';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { withStyles } from '@mui/styles';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import FlagIcon from '@mui/icons-material/Flag';
import { Alert as MuiAlert } from '@mui/lab';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import ExpenseReceiptUpload from '../Components/Expense/ExpenseReceiptUpload';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import {
    createExpense,
    getExpenseById,
    getExpenseCategories,
    getStatusesAllowed,
    updateExpense,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import Alert from '../Components/Alert';
import ConfirmationModal from '../Components/Dialog/ConfirmationModal';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import AddExpenseTab from '../Components/Expense/Tabs/AddExpenseTab';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import ReasonForSplittingForm from '../Components/Expense/ReasonForSplittingForm';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import VendorExpenseDetailsForm from '../Components/Expense/VendorExpenseDetailsForm';
import PaymentDetailsForm from '../Components/Expense/PaymentDetailsForm';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import {
    createExpenseFromExpenseEntries,
    setStatusForEntries,
    isExpenseViewOnlyByStatus,
    createCorrectionExpenseFromEntries,
} from '../Utils/ExpenseUtil';
import { uploadImages } from '../Utils/UploadImages';
import { currencies } from '../Configurations/ExpenseEntryConfig';
import { ExpenseClassEnum, ExpenseClassIdEnum } from '../Components/Expense/ExpenseClass.enum';
import { ValidationModeContext } from '../../contexts/VendorPaymentValidationModeContext';
import {
    ExpenseStatusIdsEnum,
    ExpenseStatusValuesEnum,
} from '../Components/Expense/ExpenseStatus.enum';
import ValidationModeEnum from '../Components/Expense/ValidationMode.enum';
import { ExpenseModeEnum } from '../Components/Expense/ExpenseMode.enum';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGridContainer: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    warningMessage: {
        color: COLORS.LT_SUNFLOWER,
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    flagIcon: {
        cursor: 'pointer',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
        paddingLeft: '.5rem',
    },
    leftIconContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    editButton: {
        backgroundColor: COLORS.LT_JOURNEY10,
        '&:hover': {
            backgroundColor: COLORS.LT_JOURNEY25,
        },
        color: COLORS.LT_JOURNEY,
    },
});

const defaultExpense = {
    bookingId: null,
    clinicianId: '',
    clinicianFirstName: '',
    clinicianLastName: '',
    clientId: '',
    division: '',
    state: '',
    billType: '',
    category: '',
    type: '',
    currency: 'USD',
    amount: '',
    billAmount: '',
    payAmount: '',
    reimbursedAmount: '',
    accountingProcessingNotes: '',
    label: 'Payment 1',
    status: ExpenseStatusIdsEnum.PendingSubmission,
    expenseClass: ExpenseClassEnum.Payment,
    splitTransactionComment: '',
    source: 'LtAssociate',
    vendorName: '',
    highPriority: false,
};

const VendorPayment = (props) => {
    const { classes } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser } = useContext(AdminAuthContext);
    const isViewOnly = !(isExpenseSystemAdministratorUser || isExpenseAdminUser);
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const { id } = useParams();
    const history = useHistory();
    const [alert, setAlert] = useState();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [removeTabs, setRemoveTabs] = useState(false);
    const backLink = id ? 'All Payment Request' : 'Expense Management';
    const title = id ? 'Payment Request' : 'New Payment Request';
    const expensesPath = id ? '/expenses/payment-request/review' : '/expenses';
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const [ocrUsageLogs, setOcrUsageLogs] = useState([]);
    const [ocrUsedValues, setUsedOcrValues] = useState([]);
    const [isCorrection, setIsCorrection] = useState(false);
    const isAnyExpensePosted = useMemo(() => {
        if (originalExpenseEntries?.length > 0) {
            return originalExpenseEntries.some(
                (entry) => entry.status === ExpenseStatusValuesEnum.Posted
            );
        }
        return false;
    }, [originalExpenseEntries]);
    const defaultValues = {
        fileUploads: [
            {
                invoice: { newFiles: [], existingFiles: [] },
                internal: { newFiles: [], existingFiles: [] },
            },
        ],
        expenseEntries: [defaultExpense],
    };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = methods;
    const {
        fields: expenseEntries,
        append,
        remove,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });
    const watchExpenseEntries = watch(`expenseEntries`);
    const isAnyApproved = Object.keys(watchExpenseEntries).some(
        (k) => watchExpenseEntries[k].status === ExpenseStatusValuesEnum.Approved
    );
    const [showBalanceError, setShowBalanceError] = useState(false);
    const [showBalanceWarning, setShowBalanceWarning] = useState(false);
    const hasErrors = !!Object.keys(methods.formState.errors).length;
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const [validationMode, setValidationMode] = useState(ValidationModeEnum.Save);
    const [pendingSubmit, setPendingSubmit] = useState(false);
    const [pendingSave, setPendingSave] = useState(false);
    const [openConfirmationEditModal, setOpenConfirmationEditModal] = useState(false);
    const [enableEditByModal, setEnableEditByModal] = useState(false);

    const activeExpense = useMemo(
        () =>
            Array.isArray(watchExpenseEntries) ? watchExpenseEntries[activeTabIndex] : undefined,
        [watchExpenseEntries, activeTabIndex]
    );

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({
            ...prev,
            [index]: assignment,
        }));
    };

    useEffect(() => {
        Promise.all([
            getDivisions(),
            getStatusesAllowed(ExpenseClassIdEnum.Payment),
            getExpenseCategories(ExpenseClassEnum.Payment),
        ])
            .then(async ([divs, statuses, cats]) => {
                setExpenseCategories(cats);
                setDivisions(divs);
                setStatusesAllowed(statuses);
            })
            .catch((error) => {
                console.error(error);
            });
        if (id) {
            fetchExpenseData();
        }
    }, []);

    const fetchExpenseData = async () => {
        try {
            setIsLoading(true);
            const res = await getExpenseById(id);
            const listExpenses = [];
            const aOriginalExpenseEntries = [];
            const aFileUploads = [];
            if (res.primaryExpenseId && res.primaryExpense) {
                // child split
                let childIndex = 0;
                listExpenses.push(res.primaryExpense);
                for (let i = 0; i < res.primaryExpense.splitExpenses.length; i++) {
                    const listExpensesIndex = listExpenses.push(
                        res.primaryExpense.splitExpenses[i]
                    );
                    if (res.primaryExpense.splitExpenses[i].id === id) {
                        childIndex = listExpensesIndex - 1;
                    }
                }
                setActiveTabIndex(childIndex);
            } else {
                listExpenses.push(res);
                for (let i = 0; i < res.splitExpenses.length; i++) {
                    listExpenses.push(res.splitExpenses[i]);
                }
            }

            listExpenses[0].splitExpenses = [];

            for (let i = 0; i < listExpenses.length; i++) {
                const formattedValues = formatResponse(listExpenses[i]);
                // Reset back to Null as empty string on the as the default was causing issues
                formattedValues.payDate =
                    _.isString(formattedValues.payDate) && formattedValues.payDate.length === 0
                        ? null
                        : formattedValues.payDate.substring(0, 10);
                aOriginalExpenseEntries.push({
                    ...formattedValues,
                    label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                });
                if (
                    listExpenses[i] &&
                    listExpenses[i].expenseImages &&
                    listExpenses[i].expenseImages.length >= 0
                ) {
                    // For payment requests, categorize files by isInternal flag
                    const invoiceFiles = listExpenses[i].expenseImages.filter(
                        (file) => !file.isInternal
                    );
                    const internalFiles = listExpenses[i].expenseImages.filter(
                        (file) => file.isInternal
                    );

                    aFileUploads.push({
                        invoice: {
                            newFiles: [],
                            existingFiles: invoiceFiles,
                        },
                        internal: {
                            newFiles: [],
                            existingFiles: internalFiles,
                        },
                    });
                } else {
                    // No files case for payment requests
                    aFileUploads.push({
                        invoice: { newFiles: [], existingFiles: [] },
                        internal: { newFiles: [], existingFiles: [] },
                    });
                    if (!isCorrection && listExpenses[i].expenseVerion > 0) {
                        setIsCorrection(true);
                    }
                }
            }
            setOriginalExpenseEntries(aOriginalExpenseEntries);
            setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
            const defaultValues = {
                fileUploads: aFileUploads,
                expenseEntries: aOriginalExpenseEntries,
            };
            reset(defaultValues);
        } catch (error) {
            /* empty */
        } finally {
            setIsLoading(false);
        }
    };

    const formatResponse = (res) =>
        Object.fromEntries(
            Object.entries(res).map(([key, value]) => [key, value === null ? '' : value])
        );

    const handleAddExpense = () => {
        const newExpense = {
            ...getValues('expenseEntries[0]'),
            label: 'New Payment',
            reimbursedAmount: '',
            billType: '',
            id: null,
            created: null,
            lastModified: null,
            status: ExpenseStatusIdsEnum.PendingSubmission,
            source: 'LtAssociate', // This will Ensure any Split from a source: 'Clinician' will be changed to 'LtAssociate'
            highPriority: false,
            resubmittedDate: null,
            submittedDate: null,
            mobileSubmission: false,
            salespersonName: '',
            recruiterName: '',
            amount: '',
            description: '',
        };

        const newFileUpload = {
            ...getValues('fileUploads[0]'),
        };

        if (newFileUpload) {
            // Handle the new dual structure for payment requests
            if (newFileUpload.invoice || newFileUpload.internal) {
                // Copy invoice files
                if (newFileUpload.invoice?.existingFiles?.length > 0) {
                    newFileUpload.invoice.existingFiles = newFileUpload.invoice.existingFiles
                        .filter((file) => !file.isCombinedPdf)
                        .map((file) => ({
                            ...file,
                            expenseId: null, // Clear out the Existing ExpenseId
                        }));
                }

                // Copy internal files
                if (newFileUpload.internal?.existingFiles?.length > 0) {
                    newFileUpload.internal.existingFiles = newFileUpload.internal.existingFiles
                        .filter((file) => !file.isCombinedPdf)
                        .map((file) => ({
                            ...file,
                            expenseId: null, // Clear out the Existing ExpenseId
                        }));
                }

                // Clone new invoice files
                const cloneInvoiceFiles = [];
                if (newFileUpload.invoice?.newFiles?.length > 0) {
                    newFileUpload.invoice.newFiles.forEach((file) => {
                        const cloneNewFile = new File([file], file.name, { type: file.type });
                        Object.assign(cloneNewFile, {
                            preview: URL.createObjectURL(file),
                            isInternal: false,
                        });
                        cloneInvoiceFiles.push(cloneNewFile);
                    });
                }
                newFileUpload.invoice.newFiles = cloneInvoiceFiles;

                // Clone new internal files
                const cloneInternalFiles = [];
                if (newFileUpload.internal?.newFiles?.length > 0) {
                    newFileUpload.internal.newFiles.forEach((file) => {
                        const cloneNewFile = new File([file], file.name, { type: file.type });
                        Object.assign(cloneNewFile, {
                            preview: URL.createObjectURL(file),
                            isInternal: true,
                        });
                        cloneInternalFiles.push(cloneNewFile);
                    });
                }
                newFileUpload.internal.newFiles = cloneInternalFiles;
            } else {
                // Legacy structure handling (shouldn't be needed for payment requests)
                if (newFileUpload.existingFiles && newFileUpload.existingFiles.length > 0) {
                    newFileUpload.existingFiles = newFileUpload.existingFiles
                        .filter((file) => !file.isCombinedPdf)
                        .map((file) => ({
                            ...file,
                            expenseId: null, // Clear out the Existing ExpenseId
                        }));
                }

                const cloneNewFiles = [];
                if (newFileUpload.newFiles && newFileUpload.newFiles.length > 0) {
                    newFileUpload.newFiles.forEach((file) => {
                        const cloneNewFile = new File([file], file.name, { type: file.type });
                        Object.assign(cloneNewFile, {
                            preview: URL.createObjectURL(file),
                        });
                        cloneNewFiles.push(cloneNewFile);
                    });
                }
                newFileUpload.newFiles = cloneNewFiles;
            }

            const fileUploads = getValues(`fileUploads`);
            fileUploads.push(newFileUpload);
            setValue(`fileUploads`, fileUploads);
        }

        if (newExpense.hasOwnProperty('errors')) {
            delete newExpense.errors;
        }
        if (newExpense.hasOwnProperty('statusHistory')) {
            delete newExpense.statusHistory;
        }
        if (newExpense.hasOwnProperty('relatedErrors')) {
            delete newExpense.relatedErrors;
        }
        if (newExpense.hasOwnProperty('importedData')) {
            delete newExpense.importedData;
        }
        if (!newExpense.splitTransactionComment) {
            newExpense.splitTransactionComment = '';
        }
        append(newExpense);
        setActiveTabIndex(expenseEntries.length);
    };

    const resetImages = (index) => {
        const fileUploads = getValues(`fileUploads`);
        fileUploads.splice(index, 1);
        setValue(`fileUploads`, fileUploads);
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };

    useEffect(() => {
        if (removeTabs) {
            resetImages(activeTabIndex);
            remove(activeTabIndex);
            setActiveTabIndex(activeTabIndex - 1);
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    useEffect(() => {
        setEnableEditByModal(false);
    }, [activeTabIndex]);

    const autoFillWithOCR = (ocrData) => {
        const confidenceThreshold = 0.7;
        const expenseVals = getValues(`expenseEntries[${activeTabIndex}]`);

        let usedVendorName = false;
        let usedAmount = false;
        let usedCurrency = false;

        if (
            !expenseVals.vendorName &&
            !!ocrData.vendorName?.name &&
            ocrData.vendorName?.confidence >= confidenceThreshold
        ) {
            expenseVals.vendorName = ocrData.vendorName?.name;
            usedVendorName = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                vendorName: ocrData.vendorName?.name,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        if (
            !expenseVals.amount &&
            !!ocrData.expenseAmount?.amount &&
            ocrData.expenseAmount?.confidence >= confidenceThreshold
        ) {
            if (currencies.some((c) => c.value === ocrData.expenseAmount?.currencyCode)) {
                expenseVals.currency = ocrData.expenseAmount?.currencyCode;
                usedCurrency = true;
            } else {
                expenseVals.currency = 'USD'; // Default to USD on unsupported currency code
                usedCurrency = false;
            }

            usedAmount = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                amount: ocrData.expenseAmount?.amount,
                currency: ocrData.expenseAmount?.currencyCode,
            };

            setUsedOcrValues((ocrUsedValues) => [...ocrUsedValues, ocrVal]);
        }

        const ocrUsageLogForScan = {
            expenseId: -1,
            scanId: ocrData.scanId,
            fieldUsage: {
                vendorName: usedVendorName,
                amount: usedAmount,
                currency: usedCurrency,
            },
        };

        setOcrUsageLogs((ocrUsageLogs) => [...ocrUsageLogs, ocrUsageLogForScan]);
        setValue(`expenseEntries[${activeTabIndex}]`, expenseVals);
    };

    const applySupplementalRules = (data) => {
        data.expenseEntries.forEach((entry, i) => {
            if (entry?.category !== 'Supplemental') {
                setValue(`expenseEntries.${i}.billAmount`, '0');
                entry.billAmount = '0';
                setValue(`expenseEntries.${i}.payAmount`, '0');
                entry.payAmount = '0';
            }
        });
    };

    const validateAmounts = () => {
        let hasInvalidAmount = false;

        watchExpenseEntries.forEach((entry, index) => {
            const bill = Number(entry.billAmount || 0);
            const pay = Number(entry.payAmount || 0);

            const isInvalid = bill === 0 && pay === 0;

            if (isInvalid) {
                hasInvalidAmount = true;
                setError(`expenseEntries.${index}.billAmount`, {
                    type: 'manual',
                    message: 'Payment Amount and Bill Amount cannot both be 0',
                });
                setError(`expenseEntries.${index}.payAmount`, {
                    type: 'manual',
                    message: 'Payment Amount and Bill Amount cannot both be 0',
                });
            } else {
                clearErrors(`expenseEntries.${index}.billAmount`);
                clearErrors(`expenseEntries.${index}.payAmount`);
            }
        });

        return !hasInvalidAmount;
    };

    const enableCorrectionEditMode = () => {
        setOpenConfirmationEditModal(false);
        setEnableEditByModal(true);
        const currentVersion = getValues(`expenseEntries.${activeTabIndex}.expenseVersion`);
        const updatedVersion = currentVersion ? currentVersion + 1 : 1;
        setIsCorrection(true);
        setValue(`expenseEntries.${activeTabIndex}.expenseVersion`, updatedVersion);
    };

    const onSubmit = async (data) => {
        try {
            applySupplementalRules(data);
            if (data.expenseEntries.some((e) => e?.category === 'Supplemental')) {
                const isValid = validateAmounts();
                if (!isValid) return;
            }

            setIsLoading(true);
            const entriesWithStatus = setStatusForEntries(
                data.expenseEntries,
                ExpenseStatusIdsEnum.Submitted
            );
            const newExpense = createExpenseFromExpenseEntries(entriesWithStatus);
            newExpense.ocrFieldUsage = getOcrUsage(newExpense);
            const expenseResponse = await createExpense(newExpense);
            const fileUploads = getValues(`fileUploads`);
            await uploadImages(fileUploads, expenseResponse, ExpenseModeEnum.Payment);

            history.push({
                pathname: '/expenses/submit',
                state: { prevPage: '/expenses/payment-request' },
            });
        } catch (error) {
            console.error('Error onSubmit Vendor Payment');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const validateSubmit = async () => {
        setValidationMode(ValidationModeEnum.Submit);
        setPendingSubmit(true);
    };

    const handleConfirmationEditModal = () => setOpenConfirmationEditModal(true);

    useEffect(() => {
        if (pendingSubmit && validationMode === ValidationModeEnum.Submit) {
            handleSubmit(onSubmit)();
            setPendingSubmit(false);
        }
    }, [pendingSubmit, validationMode, handleSubmit, onSubmit]);

    const validateSave = async () => {
        const isSubmitting = watchExpenseEntries.some(
            (entry) => entry.status === ExpenseStatusValuesEnum.Submitted
        );

        setValidationMode(isSubmitting ? ValidationModeEnum.Submit : ValidationModeEnum.Save);

        if (errors.balance) {
            setShowBalanceError(true);
        } else {
            clearErrors('balance');
            setShowBalanceError(false);
            setPendingSave(true);
        }
    };

    const onSave = async (data) => {
        try {
            applySupplementalRules(data);
            if (data.expenseEntries.some((e) => e?.category === 'Supplemental')) {
                const isValid = validateAmounts();
                if (!isValid) return;
            }

            setIsLoading(true);
            let entries = data.expenseEntries;
            if (isCorrection) {
                entries = createCorrectionExpenseFromEntries(data.expenseEntries);
            }
            const newExpense = createExpenseFromExpenseEntries(entries);
            newExpense.ocrFieldUsage = getOcrUsage(newExpense);

            let expenseResponse;
            if (newExpense.id) {
                expenseResponse = await updateExpense(newExpense.id, newExpense);
            } else {
                expenseResponse = await createExpense(newExpense);
            }

            // Always send all files (old + new) to backend for clubbing, but split into newFiles and existingFiles for compatibility
            const fileUploads = getValues('fileUploads').map((upload) => {
                // For invoice
                const invoiceExistingFiles = upload.invoice?.existingFiles
                    ? [...upload.invoice.existingFiles]
                    : [];
                const invoiceNewFiles = upload.invoice?.newFiles
                    ? [...upload.invoice.newFiles]
                    : [];
                // For internal
                const internalExistingFiles = upload.internal?.existingFiles
                    ? [...upload.internal.existingFiles]
                    : [];
                const internalNewFiles = upload.internal?.newFiles
                    ? [...upload.internal.newFiles]
                    : [];
                return {
                    invoice: {
                        newFiles: invoiceNewFiles,
                        existingFiles: invoiceExistingFiles,
                    },
                    internal: {
                        newFiles: internalNewFiles,
                        existingFiles: internalExistingFiles,
                    },
                };
            });

            // Only call uploadImages if there are new files to upload
            const hasAnyFiles = fileUploads.some(
                (upload) =>
                    (upload.invoice.newFiles && upload.invoice.newFiles.length > 0) ||
                    (upload.invoice.existingFiles && upload.invoice.existingFiles.length > 0) ||
                    (upload.internal.newFiles && upload.internal.newFiles.length > 0) ||
                    (upload.internal.existingFiles && upload.internal.existingFiles.length > 0)
            );

            if (hasAnyFiles && !isCorrection) {
                await uploadImages(fileUploads, expenseResponse, ExpenseModeEnum.Payment);
            }

            history.push({
                pathname: '/expenses/submit',
                state: { prevPage: '/expenses/payment-request/review' },
            });
        } catch (error) {
            console.error('Error onSave Vendor Payment');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (pendingSave) {
            handleSubmit(onSave)();
            setPendingSave(false);
        }
    }, [pendingSave, validationMode, handleSubmit, onSave]);

    const getOcrUsage = (expense) => {
        const preparedOcrUsageLogs = [];

        ocrUsageLogs.forEach((usageLog) => {
            const scanUsedValues = ocrUsedValues.filter(
                (usedValue) => usedValue.scanId === usageLog.scanId
            );

            if (
                usageLog.fieldUsage.merchantName &&
                scanUsedValues.find((val) => !!val.vendorName).vendorName !== expense.vendorName
            ) {
                usageLog.fieldUsage.vendorName = false;
            }

            if (
                usageLog.fieldUsage.amount &&
                scanUsedValues.find((val) => !!val.amount).amount !== expense.amount
            ) {
                usageLog.fieldUsage.amount = false;
            }

            if (
                usageLog.fieldUsage.currency &&
                scanUsedValues.find((val) => !!val.currency).currency !== expense.currency
            ) {
                usageLog.fieldUsage.currency = false;
            }

            preparedOcrUsageLogs.push({
                ...usageLog,
                fieldUsage: JSON.stringify(usageLog.fieldUsage),
            });
        });
        return preparedOcrUsageLogs;
    };

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <ValidationModeContext.Provider value={validationMode}>
                <FormProvider {...methods}>
                    <Grid2 container direction="column" spacing={2}>
                        <ExpenseEntryHeader
                            title={title}
                            backLink={backLink}
                            expensesPath={expensesPath}
                        />
                        {isLoading ? (
                            <CircularProgress color="primary" />
                        ) : (
                            <>
                                <Grid2 size={{ xs: 12 }}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <Grid2 container spacing={4}>
                                            <Grid2 size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                                <ExpenseTabs value={activeTabIndex}>
                                                    {watchExpenseEntries.map((expense, index) => {
                                                        const tabViewOnly =
                                                            isViewOnly ||
                                                            (isExpenseViewOnlyByStatus(
                                                                originalExpenseEntries[index]
                                                            ) &&
                                                                !enableEditByModal);
                                                        return (
                                                            <ExpenseTab
                                                                key={index}
                                                                id={`expense-tab-${index}`}
                                                                width="100%"
                                                                label={
                                                                    <ExpenseTabLabel
                                                                        isActive={
                                                                            activeTabIndex === index
                                                                        }
                                                                        index={index}
                                                                        handleRemoveTab={() =>
                                                                            handleRemoveExpense(
                                                                                index
                                                                            )
                                                                        }
                                                                        setValue={setActiveTabIndex}
                                                                        tab={expense}
                                                                        mode={
                                                                            ExpenseModeEnum.Payment
                                                                        }
                                                                        isViewOnly={tabViewOnly}
                                                                        originalPrimaryExpense={
                                                                            originalPrimaryExpense
                                                                        }
                                                                        isAnyExpensePosted={
                                                                            isAnyExpensePosted
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        );
                                                    })}
                                                    {!isViewOnly &&
                                                        !isAnyExpensePosted &&
                                                        !(
                                                            watchExpenseEntries[0].status ===
                                                                ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                            originalPrimaryExpense?.status ===
                                                                ExpenseStatusValuesEnum.NeedMoreInformation
                                                        ) && (
                                                            <AddExpenseTab
                                                                label={<AddIcon color="primary" />}
                                                                disabled={
                                                                    watchExpenseEntries[0]
                                                                        .status ===
                                                                        ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                    originalPrimaryExpense?.status ===
                                                                        ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                    watchExpenseEntries.length ===
                                                                        5 ||
                                                                    isExpenseViewOnlyByStatus(
                                                                        activeExpense
                                                                    )
                                                                }
                                                                onClick={handleAddExpense}
                                                                id="expense-tab-add"
                                                            />
                                                        )}
                                                </ExpenseTabs>
                                                {!removeTabs && (
                                                    <div>
                                                        {watchExpenseEntries.map(
                                                            (expense, index) => {
                                                                const tabViewOnly =
                                                                    isViewOnly ||
                                                                    (isExpenseViewOnlyByStatus(
                                                                        originalExpenseEntries[
                                                                            index
                                                                        ]
                                                                    ) &&
                                                                        !enableEditByModal);
                                                                return (
                                                                    <ExpenseTabPanel
                                                                        className={classes.tabPanel}
                                                                        key={`${index}-panel`}
                                                                        value={activeTabIndex}
                                                                        index={index}
                                                                        id={`expense-panel-${index}`}
                                                                    >
                                                                        <Box
                                                                            className={
                                                                                classes.leftIconContainer
                                                                            }
                                                                        >
                                                                            {isExpenseViewOnlyByStatus(
                                                                                expense
                                                                            ) &&
                                                                                !enableEditByModal && (
                                                                                    <Button
                                                                                        variant="contained"
                                                                                        className={
                                                                                            classes.editButton
                                                                                        }
                                                                                        color="primary"
                                                                                        onClick={() =>
                                                                                            setOpenConfirmationEditModal(
                                                                                                true
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </Button>
                                                                                )}
                                                                        </Box>
                                                                        <Box
                                                                            className={
                                                                                classes.iconContainer
                                                                            }
                                                                        >
                                                                            {assignmentsByIndex[
                                                                                index
                                                                            ] && (
                                                                                <Button
                                                                                    variant="outlined"
                                                                                    color="primary"
                                                                                    href={
                                                                                        assignmentsByIndex[
                                                                                            index
                                                                                        ].sfLink
                                                                                    }
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    endIcon={
                                                                                        <OpenInNew />
                                                                                    }
                                                                                    className={
                                                                                        classes.viewBookingButton
                                                                                    }
                                                                                >
                                                                                    View booking
                                                                                </Button>
                                                                            )}
                                                                            <Controller
                                                                                name={`expenseEntries[${index}].highPriority`}
                                                                                control={control}
                                                                                defaultValue={
                                                                                    expense.highPriority
                                                                                }
                                                                                render={({
                                                                                    onChange,
                                                                                    value,
                                                                                }) => (
                                                                                    <FlagIcon
                                                                                        fontSize="large"
                                                                                        className={
                                                                                            classes.flagIcon
                                                                                        }
                                                                                        onClick={() =>
                                                                                            onChange(
                                                                                                !value
                                                                                            )
                                                                                        }
                                                                                        style={{
                                                                                            color: value
                                                                                                ? COLORS.LT_PEPPER
                                                                                                : COLORS.LT_MIDNIGHT25,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </Box>
                                                                        <div
                                                                            className={
                                                                                classes.alertContainer
                                                                            }
                                                                        >
                                                                            {expense.errors &&
                                                                                expense.errors
                                                                                    .length > 0 && (
                                                                                    <ExpenseNeedsReviewAlert
                                                                                        expense={
                                                                                            expense
                                                                                        }
                                                                                    />
                                                                                )}
                                                                        </div>

                                                                        <Grid2
                                                                            container
                                                                            direction="column"
                                                                            className={
                                                                                classes.formGridContainer
                                                                            }
                                                                        >
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <BookingDetailsForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    divisions={
                                                                                        divisions
                                                                                    }
                                                                                    isSGA={[]}
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    onAssignmentChange={
                                                                                        handleAssignmentChange
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <ExpenseReceiptUploadForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    onOCRUpload={
                                                                                        autoFillWithOCR
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <VendorExpenseDetailsForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    expenseCategories={
                                                                                        expenseCategories
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <PaymentDetailsForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <InternalNotesForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    statusesAllowed={
                                                                                        statusesAllowed
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    numTabs={
                                                                                        watchExpenseEntries.length
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                {(() => {
                                                                                    // Calculate values needed for ExpenseReceiptUpload
                                                                                    const originalStatus =
                                                                                        expense.id &&
                                                                                        originalExpenseEntries?.length >
                                                                                            0
                                                                                            ? originalExpenseEntries.find(
                                                                                                  (
                                                                                                      entry
                                                                                                  ) =>
                                                                                                      entry.id ===
                                                                                                      expense.id
                                                                                              )
                                                                                                  ?.status ||
                                                                                              expense.status
                                                                                            : expense.status;
                                                                                    const currentStatus =
                                                                                        watchExpenseEntries[
                                                                                            index
                                                                                        ]?.status;
                                                                                    const isPosted =
                                                                                        originalStatus ===
                                                                                            ExpenseStatusValuesEnum.Posted ||
                                                                                        (currentStatus ===
                                                                                            ExpenseStatusValuesEnum.OnHoldReimbursement &&
                                                                                            originalStatus ===
                                                                                                ExpenseStatusValuesEnum.Posted);
                                                                                    const isPendingSubmission =
                                                                                        expense.status ===
                                                                                        ExpenseStatusValuesEnum.PendingSubmission;
                                                                                    const expenseId =
                                                                                        expense.id;

                                                                                    return (
                                                                                        <ExpenseReceiptUpload
                                                                                            index={
                                                                                                index
                                                                                            }
                                                                                            expense={
                                                                                                expense
                                                                                            }
                                                                                            originalExpenseEntries={
                                                                                                originalExpenseEntries
                                                                                            }
                                                                                            mode={
                                                                                                ExpenseModeEnum.Payment
                                                                                            }
                                                                                            isPosted={
                                                                                                isPosted
                                                                                            }
                                                                                            isPendingSubmission={
                                                                                                isPendingSubmission
                                                                                            }
                                                                                            isViewOnly={
                                                                                                tabViewOnly
                                                                                            }
                                                                                            onOCRUpload={
                                                                                                autoFillWithOCR
                                                                                            }
                                                                                            expenseId={
                                                                                                expenseId
                                                                                            }
                                                                                            attachmentType="internal"
                                                                                            isInternal
                                                                                            showSectionTitle
                                                                                            sectionTitle="Internal attachments"
                                                                                            sectionRequired={
                                                                                                false
                                                                                            }
                                                                                        />
                                                                                    );
                                                                                })()}
                                                                            </Grid2>
                                                                            <Grid2
                                                                                size={{ xs: 12 }}
                                                                            >
                                                                                <ReasonForSplittingForm
                                                                                    index={index}
                                                                                    expense={
                                                                                        expense
                                                                                    }
                                                                                    originalExpenseEntries={
                                                                                        originalExpenseEntries
                                                                                    }
                                                                                    isViewOnly={
                                                                                        tabViewOnly
                                                                                    }
                                                                                    mode={
                                                                                        ExpenseModeEnum.Payment
                                                                                    }
                                                                                />
                                                                            </Grid2>
                                                                        </Grid2>
                                                                    </ExpenseTabPanel>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                )}
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 5, md: 4, lg: 3 }}>
                                                <div className={classes.summary}>
                                                    <ExpenseSummary
                                                        setError={setError}
                                                        clearErrors={clearErrors}
                                                        setShowBalanceError={setShowBalanceError}
                                                        showBalanceError={showBalanceError}
                                                        setShowBalanceWarning={
                                                            setShowBalanceWarning
                                                        }
                                                        isAnyApproved={isAnyApproved}
                                                    />
                                                    {showBalanceError && (
                                                        <Typography color="error">
                                                            {errors.balance.message}
                                                        </Typography>
                                                    )}
                                                    {showBalanceWarning && (
                                                        <Typography
                                                            className={classes.warningMessage}
                                                            color="inherit"
                                                        >
                                                            Warning: The check amount exceeds the
                                                            submitted amount. Please review the
                                                            values before saving or submitting.
                                                        </Typography>
                                                    )}
                                                </div>
                                            </Grid2>
                                        </Grid2>
                                    </form>
                                </Grid2>
                                {hasErrors > 0 && (
                                    <Grid2 container className={classes.alertContainer}>
                                        <Grid2 size={{ xs: 12, sm: 7, md: 8, lg: 9 }}>
                                            <MuiAlert severity="error">
                                                Please review the highlighted fields above and
                                                correct the errors to submit your expense.
                                            </MuiAlert>
                                        </Grid2>
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 12 }}>
                                    <ExpenseButtons
                                        validateSubmit={validateSubmit}
                                        validateSave={validateSave}
                                        expensesPath={expensesPath}
                                        alwaysShowSave
                                        isViewOnly={isExpenseViewOnlyByStatus(
                                            originalExpenseEntries[activeTabIndex]
                                        )}
                                        enableEditByModal={enableEditByModal}
                                    />
                                </Grid2>
                            </>
                        )}
                    </Grid2>
                </FormProvider>
            </ValidationModeContext.Provider>
            <Alert alert={alert} setAlert={setAlert} />
            <ConfirmationModal
                open={openConfirmationEditModal}
                text="You are attempting to edit an approved expense, do you want to continue?"
                onYes={enableCorrectionEditMode}
                onNo={() => {
                    setOpenConfirmationEditModal(false);
                    setEnableEditByModal(false);
                }}
            />
        </Container>
    );
};

export default withStyles(styles)(VendorPayment);
